import { useRef, useCallback } from 'react';
import { useMotionValue, useSpring } from 'motion/react';

interface MagneticOptions {
  /** Intensidade do efeito (0–1). */
  strength?: number;
  /** Stiffness da mola do efeito. */
  stiffness?: number;
  /** Damping da mola. */
  damping?: number;
}

/**
 * Retorna ref e MotionValues de x/y para aplicar efeito magnético em um elemento.
 * O elemento "puxa" o cursor quando o mouse está sobre ele.
 *
 * @example
 * const { ref, x, y, handlers } = useMagnetic({ strength: 0.4 });
 * <motion.button ref={ref} style={{ x, y }} {...handlers}>
 *   Hover me
 * </motion.button>
 */
export function useMagnetic<T extends HTMLElement = HTMLElement>({
  strength = 0.35,
  stiffness = 180,
  damping = 20,
}: MagneticOptions = {}) {
  const ref = useRef<T>(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);

  const x = useSpring(rawX, { stiffness, damping });
  const y = useSpring(rawY, { stiffness, damping });

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<T>) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      rawX.set((e.clientX - cx) * strength);
      rawY.set((e.clientY - cy) * strength);
    },
    [rawX, rawY, strength],
  );

  const handleMouseLeave = useCallback(() => {
    rawX.set(0);
    rawY.set(0);
  }, [rawX, rawY]);

  return {
    ref,
    x,
    y,
    handlers: {
      onMouseMove: handleMouseMove,
      onMouseLeave: handleMouseLeave,
    },
  };
}
